import { type OpRecord } from '@dl/protocol';
import {
  ENTITY_META_PREDICATE,
  FactPayloadSchema,
  foldFactChainFull,
  type FactPayload,
} from '@dl/shared';

import { normalizeName } from './normalize.js';
import { type SearchSqlBackend } from './sql.js';
import {
  type CandidateRef,
  type SearchLane,
  type SearchScope,
} from './types.js';

/**
 * The shared name index — a single table maintained by the op-log replay
 * machinery. One row per entity; the value column is `normalized_name`
 * (NFD + strip diacritics + lowercased). The "name" predicate is the
 * canonical name source today; this is intentionally narrow — packs
 * declaring other display-name predicates is a Phase 2 concern.
 *
 * Schema parity is enforced by mirrored migrations:
 *   - apps/do-workspace/migrations/0002_search_name_index.sql
 *   - packages/replica/src/schema.ts (version 5)
 *
 * The index is REBUILDABLE — the op log is the source of truth. The
 * replay test (B5 / #10) verifies that dropping this table and replaying
 * the op log produces an identical index.
 */
export const ENTITY_NAME_INDEX_TABLE = 'entity_name_index';
export const ENTITY_NAME_INDEX_DDL = `
  CREATE TABLE IF NOT EXISTS ${ENTITY_NAME_INDEX_TABLE} (
    entity_id        TEXT PRIMARY KEY,
    normalized_name  TEXT NOT NULL,
    display_name     TEXT NOT NULL
  );
  CREATE INDEX IF NOT EXISTS entity_name_index_normalized
    ON ${ENTITY_NAME_INDEX_TABLE} (normalized_name);
`;

/** The predicate the index treats as the entity's display name. */
export const NAME_PREDICATE = 'name';

/**
 * Upsert one entity's name into the index, normalising the value.
 *
 * Called from:
 *   - the op-accept path (server / replica) on every `fact.create` or
 *     `fact.edit` whose predicate is `name`,
 *   - the replay-rebuild path (B5 / #10).
 */
export function upsertNameIndex(
  sql: SearchSqlBackend,
  entity_id: string,
  display_name: string,
): void {
  const normalized = normalizeName(display_name);
  if (normalized.length === 0) {
    deleteNameIndex(sql, entity_id);
    return;
  }
  sql.exec(
    `INSERT INTO ${ENTITY_NAME_INDEX_TABLE} (entity_id, normalized_name, display_name)
       VALUES (?, ?, ?)
     ON CONFLICT (entity_id) DO UPDATE SET
       normalized_name = excluded.normalized_name,
       display_name    = excluded.display_name;`,
    entity_id,
    normalized,
    display_name,
  );
}

/** Delete one entity's name index row (called on soft-delete or empty-name edit). */
export function deleteNameIndex(sql: SearchSqlBackend, entity_id: string): void {
  sql.exec(`DELETE FROM ${ENTITY_NAME_INDEX_TABLE} WHERE entity_id = ?;`, entity_id);
}

/**
 * Rebuild the name index from the op log. Used by the replay test
 * (B5 / #10) and any future "search index drift" recovery path.
 *
 * The fold is intentionally simple: group fact ops on the `name`
 * predicate by entity, fold via `foldFactChainFull`, and upsert the
 * folded string value. Non-string name values are skipped (the predicate
 * registry — when wired in Phase 2 — will reject them at write time;
 * until then we do the defensive thing here).
 */
export function rebuildNameIndex(
  sql: SearchSqlBackend,
  ops: Iterable<OpRecord<unknown>>,
): void {
  sql.exec(`DELETE FROM ${ENTITY_NAME_INDEX_TABLE};`);

  const chains = new Map<string, FactPayload[]>();
  for (const op of ops) {
    if (op.predicate !== NAME_PREDICATE) continue;
    // (NAME_PREDICATE is never ENTITY_META_PREDICATE, but the constant is
    // imported so future name-source extensions don't accidentally
    // collide with the meta predicate.)
    void ENTITY_META_PREDICATE;
    const payload = op.payload as { kind?: unknown } | null | undefined;
    if (!payload || typeof payload !== 'object') continue;
    const kind = (payload as { kind?: unknown }).kind;
    if (typeof kind !== 'string' || !kind.startsWith('fact.')) continue;
    const parsed = FactPayloadSchema.parse(payload);
    const chain = chains.get(op.subject) ?? [];
    chain.push(parsed);
    chains.set(op.subject, chain);
  }

  for (const [entity_id, chain] of chains) {
    const folded = foldFactChainFull(chain);
    if (!folded) continue;
    if (folded.soft_deleted) continue;
    if (typeof folded.value !== 'string') continue;
    upsertNameIndex(sql, entity_id, folded.value);
  }
}

/**
 * Construct a name-lane SearchLane backed by `sql`. The lane returns
 * the top `per_lane_limit` (default 50) entity candidates ranked by:
 *   - exact match (lane_score 3.0)
 *   - prefix match (2.0)
 *   - substring match (1.0)
 *
 * Empty query returns no candidates. The lane never throws on a malformed
 * query — callers can pass arbitrary user input.
 */
export function nameLane(sql: SearchSqlBackend): SearchLane {
  return {
    name: 'name',
    weight: 1,
    query(q: string, scope: SearchScope): CandidateRef[] {
      const needle = normalizeName(q);
      if (needle.length === 0) return [];
      const limit = scope.per_lane_limit ?? 50;

      // SQL: pull every row whose normalized_name CONTAINS the needle —
      // then score in TS so we don't have to express the tri-score logic
      // in a CASE expression with multiple LIKE patterns (which would
      // additionally have to escape `_` and `%` in the user query).
      const likeNeedle = `%${escapeLike(needle)}%`;
      const rows = sql.all<{ entity_id: string; normalized_name: string }>(
        `SELECT entity_id, normalized_name
           FROM ${ENTITY_NAME_INDEX_TABLE}
          WHERE normalized_name LIKE ? ESCAPE '\\'
          LIMIT ?;`,
        likeNeedle,
        limit * 4, // overshoot — TS sorts/caps after scoring
      );

      const scored: { entity_id: string; score: number }[] = [];
      for (const row of rows) {
        const n = row.normalized_name;
        let score: number;
        if (n === needle) score = 3;
        else if (n.startsWith(needle)) score = 2;
        else if (n.includes(needle)) score = 1;
        else continue; // shouldn't happen given LIKE — defensive
        scored.push({ entity_id: row.entity_id, score });
      }
      scored.sort((a, b) => b.score - a.score);
      return scored.slice(0, limit).map((s) => ({
        kind: 'entity',
        entity_id: s.entity_id,
        lane_name: 'name',
        lane_score: s.score,
      }));
    },
  };
}

function escapeLike(s: string): string {
  return s.replace(/[\\%_]/g, (ch) => `\\${ch}`);
}
